/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-09 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Button, TextStyle, Icon, Link, Stack, Layout } from '@shopify/polaris';
import {
  CaretDownMinor,
  CaretUpMinor,
  QuestionMarkMajor,
  PlayCircleMajor,
  ExternalSmallMinor
} from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import Themes from './themes';
import ThemeSetupForm from './theme-setup-form';
import MainMenu from 'src/components/containers/main-menu';
import DidYouKnow from 'src/components/views/theme-setup/did-you-know';
import ThemeSetupError from 'src/components/views/theme-setup/theme-setup-error';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import ThemeUpgradeFlow1Notification from './theme-upgrade-flow-1-notification';
import ThemeUpgradeFlow2Notification from './theme-upgrade-flow-2-notification';
import { URL } from 'src/constants/links';

type Props = {
  installStatus: string,
  installAppStatus: string,
  themeSetupV2: boolean
};

type ComposedProps = Props & WithI18nProps;

type State = {
  toggleThemes: boolean,
  youtubeModal: boolean
};

class ThemeSetup extends Component<ComposedProps, State> {
  state = {
    toggleThemes: false,
    youtubeModal: false
  };

  ref: { current: Object } = React.createRef();

  render() {
    const { toggleThemes, youtubeModal } = this.state;
    const { i18n, installStatus, installAppStatus, themeSetupV2 } = this.props;

    return (
      <div className="BC-Page BC-Page__Theme-Setup">
        <MainMenu menuId="menu-integration-theme-setup" />
        <Page
          title={i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.title')}
          titleMetadata={
            <div className="toggle-button-show">
              <Button
                monochrome
                outline
                icon={toggleThemes ? CaretUpMinor : CaretDownMinor}
                onClick={this.toggleThemesContent}>
                {toggleThemes
                  ? i18n.translate('AdminHTML.Common.Action.show')
                  : i18n.translate('AdminHTML.Common.Action.hide')}
              </Button>
            </div>
          }>
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                <ThemeUpgradeFlow1Notification />
                <ThemeUpgradeFlow2Notification />

                <ExceededPlanNotification />

                {installStatus === 'error' || installAppStatus === 'theme-setup-error' ? (
                  <ThemeSetupError />
                ) : (
                  ''
                )}
              </div>
            </Layout.Section>
            <Layout.Section>
              <div className="show-hide-content-wraper">
                <div
                  ref={this.ref}
                  className="themesBox show-hide-content"
                  style={
                    toggleThemes
                      ? {
                          marginTop: `-${this.ref.current.offsetHeight}px`,
                          marginBottom: '1.5rem',
                          opacity: 0
                        }
                      : {
                          marginTop: 0,
                          marginBottom: '2.5rem',
                          opacity: 1
                        }
                  }>
                  {/* Did you know? */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <DidYouKnow />
                  </div>

                  <Themes />
                </div>
              </div>

              <div className="headingWrapper">
                <span className="headingStyle">
                  {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.themeSetup')}
                </span>
                <Stack spacing="tight">
                  <span className="titleMetadata">
                    <a href={URL['ThemeSetupDoc']} target="_blank" rel="noopener noreferrer">
                      <Icon source={QuestionMarkMajor} color="base" />
                    </a>
                  </span>
                  <span className="titleMetadata">
                    <button onClick={this.handleOpenYoutubeModal}>
                      <Icon source={PlayCircleMajor} color="base" />
                    </button>
                  </span>
                </Stack>
              </div>

              <div className="themeSetupBox">
                <ThemeSetupForm />
              </div>
              <div className="BC-Page BC-Page__Bottom-Text">
                <TextStyle>
                  {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.bottomText', {
                    t1: (
                      <Link external url={URL['ManualSetup'.concat(themeSetupV2 ? 'V2' : 'V1')]}>
                        {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.bottomText_t1')}
                      </Link>
                    ),
                    externalIcon: (
                      <span className="external-link">
                        <Button icon={ExternalSmallMinor} plain />
                      </span>
                    )
                  })}
                </TextStyle>
              </div>

              <div className="BC-Page BC-Page__Bottom-Text">
                <TextStyle>
                  {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.bottomTextTAE', {
                    t1: (
                      <Link external url={URL.EnableAppBlockTAE}>
                        {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.bottomTextTAE_t1')}
                      </Link>
                    ),
                    externalIcon: (
                      <span className="external-link">
                        <Button icon={ExternalSmallMinor} plain />
                      </span>
                    )
                  })}
                </TextStyle>
              </div>
            </Layout.Section>
          </Layout>
        </Page>
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['ThemeSetupTut']}
          title={i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.youtubeModal_Title')}
          onClose={this.handleCloseYoutubeModal}
        />
      </div>
    );
  }

  toggleThemesContent = () => {
    this.setState(({ toggleThemes }) => {
      return { toggleThemes: !toggleThemes };
    });
  };

  handleOpenYoutubeModal = () => this.setState({ youtubeModal: true });
  handleCloseYoutubeModal = () => this.setState({ youtubeModal: false });
}

const mapStateToProps = (state, props) => ({
  installStatus: _.get(state.install, 'installStatus', ''),
  installAppStatus: _.get(state.oauth.tenantApp, 'installStatus', ''),
  themeSetupV2: _.get(state.oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false)
});

export default connect(mapStateToProps)(withI18n()(ThemeSetup));
